import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  WSButton,
  WSButtons,
  WSElement,
  WSStripeInput,
  WSStripeInputRef
} from "@wingspanhq/fe-component-library";
import { PayoutDestinationType } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo, useRef, useState } from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_INSTANT_PAYOUT } from "../../../query/payments/keys";
import { useInstantPayoutQuery } from "../../../query/payments/queries";
import { paymentsService } from "../../../services/payments";

type SetupInstantAccountFormProps = {
  onSubmit?: () => void;
};

export const SetupInstantAccountForm: React.FC<SetupInstantAccountFormProps> = ({
  onSubmit
}) => {
  const userId = useUserId();
  const stripeInput = useRef<WSStripeInputRef>(null);
  const stripePromise = useMemo(
    () => loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`),
    []
  );
  const [stripeInputState, setStripeInputState] = useState<
    StripeCardElementChangeEvent | undefined
  >();
  const instantPayoutQuery = useInstantPayoutQuery({ retry: false });

  const [submit, submitMeta] = useWSMutation(
    async () => {
      if (stripeInputState?.complete) {
        const result = await stripeInput?.current?.createCardToken({
          currency: "USD"
        });
        if (result?.token) {
          const instantAccount = await paymentsService.banking.instantPayout.create(
            {
              externalPayoutAccountToken: result.token.id
            }
          );

          await paymentsService.payoutSettings.update(userId, {
            instant: {
              destinationType: PayoutDestinationType.Card,
              destinationId: instantAccount.instantPayoutAccountId
            }
          });

          onSubmit?.();
        } else {
          throw new Error("No token generated");
        }
      } else {
        throw new Error("Invalid card data");
      }
    },
    {
      dependencies: [QUERY_INSTANT_PAYOUT]
    }
  );

  return (
    <WSElement>
      <WSStripeInput
        ref={stripeInput}
        stripeInstance={stripePromise}
        onInputChange={setStripeInputState}
        name="creditCard"
        mb="M"
      />
      <WSErrorMessage
        error={submitMeta.error}
        contextKey="InstantDepositAccount"
        mb="M"
      />
      <WSButtons format="modal" forceFullWidth>
        <WSButton type="button" onClick={submit} loading={submitMeta.isLoading}>
          {!!instantPayoutQuery.data ? "Update" : "Add"} instant deposit account
        </WSButton>
      </WSButtons>
    </WSElement>
  );
};
